import { normalizeComponentId } from '@utils';
import { registerTabInjection } from '@core/pages/injected-tabs/tabInjectionRegistry';
import { registerLocalization } from "@localisation/localizationUtils";
import localizationMessages from '../localization.json';
export { normalizeComponentId };
// ── Unified registry ───────────────────────────────────────────────────────
window.registerComponent = (c) => {
    var _a;
    if (!((_a = c.modes) === null || _a === void 0 ? void 0 : _a.length))
        c = Object.assign(Object.assign({}, c), { modes: ['widget'] });
    // Pre-compute normalizedId once at registration — lookup never re-derives it
    if (!c.normalizedId)
        c = Object.assign(Object.assign({}, c), { normalizedId: normalizeComponentId(c.id) });
    if (!window.Components)
        window.Components = [];
    window.Components.push(c);
    window.dispatchEvent(new CustomEvent('uxp:component-registered', { detail: c }));
};
// ── Backward-compat adapters ───────────────────────────────────────────────
// These receive already-prefixed IDs from the per-project uxp.ts wrapper
// (e.g. id = 'iviva-system-app/widget/sensor-chart')
window.registerWidget = (widget) => {
    var _a, _b;
    window.registerComponent({
        id: widget.id,
        name: (_b = (_a = widget.name) !== null && _a !== void 0 ? _a : widget.label) !== null && _b !== void 0 ? _b : widget.title,
        description: widget.description,
        component: widget.widget,
        modes: ['widget'],
        configs: widget.configs,
        icon: widget.icon,
        tags: widget.tags,
        sourceUrl: widget.sourceUrl,
        isTemplate: widget.isTemplate,
        isDefaultTemplate: widget.isDefaultTemplate,
        defaultProps: widget.defaultProps,
        props: widget.props,
        templateKey: widget.templateKey,
        templateType: widget.templateType,
        category: widget.category,
        usecaseId: widget.usecaseId,
        usecaseName: widget.usecaseName,
        definitionPath: widget.definitionPath,
        vendor: widget.vendor,
        installed: widget.installed,
        deleted: widget.deleted,
        external: widget.external,
        appRoles: widget.appRoles,
        userGroups: widget.userGroups,
        showUnauthorizedError: widget.showUnauthorizedError,
    });
};
window.registerUI = (ui) => {
    var _a;
    window.registerComponent({
        id: ui.id,
        name: (_a = ui.label) !== null && _a !== void 0 ? _a : ui.title,
        description: ui.description,
        component: ui.component,
        modes: ['ui'],
        props: ui.uiProps,
        showDefaultHeader: ui.showDefaultHeader,
        appRoles: ui.appRoles,
        userGroups: ui.userGroups,
        showUnauthorizedError: ui.showUnauthorizedError,
    });
};
/**
 * Lucy model-designer block definition. Registered as a 'lucy-block'
 * component; the designer merges `blockConfig` over the block's ObjectMap
 * catalog entry. See iviva-net-core/docs/apps/lucy-connector-blocks.md.
 */
window.registerLucyBlock = (def) => {
    var _a;
    if (!(def === null || def === void 0 ? void 0 : def.type)) {
        console.warn('[UXP] registerLucyBlock: a block definition requires a `type` (e.g. "connector:MyApp:MyUnit")');
        return;
    }
    window.registerComponent({
        id: def.type,
        name: (_a = def.title) !== null && _a !== void 0 ? _a : def.type,
        component: NoopComponent,
        modes: ['lucy-block'],
        blockConfig: def,
    });
};
// lucy-block entries never render through ComponentRenderer — placeholder to
// satisfy the required `component` field.
const NoopComponent = () => null;
window.registerBackgroundSurface = (surface) => {
    window.registerComponent({
        id: surface.type,
        name: surface.label,
        description: surface.description,
        component: surface.component,
        modes: ['background'],
        configs: surface.props
            ? { props: surface.props, configPanel: surface.configPanel }
            : undefined,
        compatibleObjects: surface.compatibleObjects,
    });
};
// ── Injected object-detail tabs ────────────────────────────────────────────
// Same shape as registerComponent: the per-project uxp.ts wrapper merges the app's
// bundle.json `objectTabs` entry onto the partial call, generates the composite
// `bundleId/tabId` id, and calls this global with the merged result.
window.registerTabInjection = (t) => {
    var _a;
    registerTabInjection(Object.assign(Object.assign({}, t), { 
        // bundle.json declares a single componentId (wrapper-prefixed); adapt to components[]
        components: (_a = t.components) !== null && _a !== void 0 ? _a : (t.componentId ? [{ componentId: t.componentId, priority: 1 }] : []) }));
    window.dispatchEvent(new CustomEvent('uxp:tab-injection-registered', { detail: t }));
};
// ── Legacy array shims ─────────────────────────────────────────────────────
// Some older consumers (e.g. lucy-canvas/RemoteComponent) read window.RenderUIItems
// or window.Widgets directly. These getters make those reads transparent proxies into
// window.Components so no separate array needs to be maintained.
Object.defineProperty(window, 'RenderUIItems', {
    get: () => { var _a; return ((_a = window.Components) !== null && _a !== void 0 ? _a : []).filter((c) => { var _a; return (_a = c.modes) === null || _a === void 0 ? void 0 : _a.includes('ui'); }); },
    configurable: true,
    enumerable: true,
});
Object.defineProperty(window, 'Widgets', {
    get: () => { var _a; return ((_a = window.Components) !== null && _a !== void 0 ? _a : []).filter((c) => { var _a; return (_a = c.modes) === null || _a === void 0 ? void 0 : _a.includes('widget'); }); },
    configurable: true,
    enumerable: true,
});
// ── Deprecated no-ops ──────────────────────────────────────────────────────
/** @deprecated registerLink is not supported in V5. Navigation links are now defined via route/navigation configuration. */
window.registerLink = (_link) => {
    console.warn('[UXP] registerLink is deprecated and has no effect in V5. Define navigation links via the navigation configuration instead.');
};
/** @deprecated registerMenuItem is not supported in V5. */
window.registerMenuItem = (_menuItem) => {
    console.warn('[UXP] registerMenuItem is deprecated and has no effect in V5.');
};
window.registerLocalization = registerLocalization;
registerLocalization(localizationMessages);
